import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const COLORS = {
  primary: [15, 23, 42],      // slate-900
  accent: [245, 158, 11],     // amber-500
  success: [16, 185, 129],    // emerald-500
  danger: [239, 68, 68],      // red-500
  gray: [100, 116, 139],      // slate-500
  lightGray: [241, 245, 249], // slate-100
  white: [255, 255, 255]
};

const formatCurrency = (value) => {
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value || 0);
};

const monthNames = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 
                    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

const addHeader = (doc, parkName, periodLabel, reportTitle) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  
  // Header background
  doc.setFillColor(...COLORS.primary);
  doc.rect(0, 0, pageWidth, 45, 'F');
  
  // Logo text
  doc.setTextColor(...COLORS.white);
  doc.setFontSize(24);
  doc.setFont('helvetica', 'bold');
  doc.text('NAAVE', 20, 25);
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text('Gestión de Parques Industriales', 20, 35);
  
  // Report info
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text(reportTitle, pageWidth - 20, 20, { align: 'right' });
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(parkName, pageWidth - 20, 30, { align: 'right' });
  doc.text(periodLabel, pageWidth - 20, 38, { align: 'right' });
  
  return 55; // Return Y position after header
};

const addFooter = (doc) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const totalPages = doc.internal.getNumberOfPages();
  
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    
    // Footer line
    doc.setDrawColor(...COLORS.lightGray);
    doc.line(20, pageHeight - 20, pageWidth - 20, pageHeight - 20);
    
    // Footer text
    doc.setFontSize(8);
    doc.setTextColor(...COLORS.gray);
    doc.text(`Generado: ${new Date().toLocaleDateString('es-MX')}`, 20, pageHeight - 12);
    doc.text(`Página ${i} de ${totalPages}`, pageWidth - 20, pageHeight - 12, { align: 'right' });
  }
};

export const generateIncomeExpenseReportPDF = (parkName, year, incomeReport, periodLabel = null) => {
  const doc = new jsPDF('p', 'mm', 'a4');
  const displayPeriod = periodLabel || `Año ${year}`;
  let yPos = addHeader(doc, parkName, displayPeriod, 'REPORTE DE INGRESOS VS GASTOS');
  
  // Summary cards
  doc.setFillColor(...COLORS.lightGray);
  doc.rect(15, yPos, 180, 30, 'F');
  
  const summaryItems = [
    { label: 'Cuotas Generadas', value: incomeReport?.totals?.cuotas_generadas, color: COLORS.primary },
    { label: 'Ingresos Cobrados', value: incomeReport?.totals?.ingresos_cobrados, color: COLORS.success },
    { label: 'Gastos Totales', value: incomeReport?.totals?.gastos, color: COLORS.danger },
    { label: 'Resultado Neto', value: incomeReport?.totals?.resultado, color: incomeReport?.totals?.resultado >= 0 ? COLORS.success : COLORS.danger }
  ];
  
  const cardWidth = 45;
  summaryItems.forEach((item, index) => {
    const xPos = 15 + (index * cardWidth);
    doc.setFontSize(8);
    doc.setTextColor(...COLORS.gray);
    doc.text(item.label, xPos + 5, yPos + 10);
    
    doc.setFontSize(12);
    doc.setTextColor(...item.color);
    doc.setFont('helvetica', 'bold');
    doc.text(formatCurrency(item.value), xPos + 5, yPos + 22);
    doc.setFont('helvetica', 'normal');
  });
  
  yPos += 40;
  
  // Monthly table
  doc.setFontSize(12);
  doc.setTextColor(...COLORS.primary);
  doc.setFont('helvetica', 'bold');
  doc.text('Detalle por Período', 15, yPos);
  yPos += 8;
  
  const tableData = incomeReport?.data?.map((row) => [
    monthNames[row.mes - 1],
    formatCurrency(row.cuotas_generadas),
    formatCurrency(row.ingresos_cobrados),
    formatCurrency(row.gastos),
    formatCurrency(row.resultado)
  ]) || [];
  
  // Add totals row
  const totalLabel = incomeReport?.data?.length === 12 ? 'TOTAL ANUAL' : 'TOTAL PERÍODO';
  tableData.push([
    totalLabel,
    formatCurrency(incomeReport?.totals?.cuotas_generadas),
    formatCurrency(incomeReport?.totals?.ingresos_cobrados),
    formatCurrency(incomeReport?.totals?.gastos),
    formatCurrency(incomeReport?.totals?.resultado)
  ]);
  
  autoTable(doc, {
    startY: yPos,
    head: [['Mes', 'Cuotas Generadas', 'Ingresos Cobrados', 'Gastos', 'Resultado']],
    body: tableData,
    theme: 'grid',
    headStyles: {
      fillColor: COLORS.primary,
      textColor: COLORS.white,
      fontStyle: 'bold',
      halign: 'center'
    },
    columnStyles: {
      0: { halign: 'left', fontStyle: 'bold' },
      1: { halign: 'right' },
      2: { halign: 'right' },
      3: { halign: 'right' },
      4: { halign: 'right' }
    },
    styles: {
      fontSize: 9,
      cellPadding: 4
    },
    alternateRowStyles: {
      fillColor: COLORS.lightGray
    },
    didParseCell: (data) => {
      // Style totals row
      if (data.row.index === tableData.length - 1) {
        data.cell.styles.fillColor = COLORS.primary;
        data.cell.styles.textColor = COLORS.white;
        data.cell.styles.fontStyle = 'bold';
      }
    }
  });
  
  addFooter(doc);
  const periodSuffix = periodLabel ? periodLabel.replace(/\s+/g, '_') : year;
  doc.save(`Reporte_Ingresos_Gastos_${parkName.replace(/\s+/g, '_')}_${periodSuffix}.pdf`);
};

export const generateBudgetReportPDF = (parkName, year, budgetReport, periodLabel = null) => {
  const doc = new jsPDF('p', 'mm', 'a4');
  const displayPeriod = periodLabel || `Año ${year}`;
  let yPos = addHeader(doc, parkName, displayPeriod, 'REPORTE DE GASTO VS PRESUPUESTO');
  
  // Summary cards
  doc.setFillColor(...COLORS.lightGray);
  doc.rect(15, yPos, 180, 30, 'F');
  
  const summaryItems = [
    { label: 'Presupuesto Total', value: budgetReport?.totals?.presupuesto, color: COLORS.primary },
    { label: 'Gasto Real', value: budgetReport?.totals?.gasto_real, color: COLORS.danger },
    { label: '% Ejecutado', value: `${budgetReport?.totals?.porcentaje?.toFixed(1)}%`, isPercent: true, color: budgetReport?.totals?.porcentaje > 100 ? COLORS.danger : COLORS.success }
  ];
  
  const cardWidth = 60;
  summaryItems.forEach((item, index) => {
    const xPos = 15 + (index * cardWidth);
    doc.setFontSize(8);
    doc.setTextColor(...COLORS.gray);
    doc.text(item.label, xPos + 5, yPos + 10);
    
    doc.setFontSize(14);
    doc.setTextColor(...item.color);
    doc.setFont('helvetica', 'bold');
    doc.text(item.isPercent ? item.value : formatCurrency(item.value), xPos + 5, yPos + 22);
    doc.setFont('helvetica', 'normal');
  });
  
  yPos += 40;
  
  // Budget table
  doc.setFontSize(12);
  doc.setTextColor(...COLORS.primary);
  doc.setFont('helvetica', 'bold');
  doc.text('Detalle por Categoría', 15, yPos);
  yPos += 8;
  
  const tableData = budgetReport?.data?.map(row => [
    row.categoria,
    row.subcategoria || '-',
    formatCurrency(row.presupuesto_anual),
    formatCurrency(row.gasto_real),
    `${row.porcentaje_ejecutado.toFixed(1)}%`,
    formatCurrency(row.diferencia)
  ]) || [];
  
  // Add totals row
  tableData.push([
    'TOTAL',
    '',
    formatCurrency(budgetReport?.totals?.presupuesto),
    formatCurrency(budgetReport?.totals?.gasto_real),
    `${budgetReport?.totals?.porcentaje?.toFixed(1)}%`,
    formatCurrency(budgetReport?.totals?.diferencia)
  ]);
  
  autoTable(doc, {
    startY: yPos,
    head: [['Categoría', 'Subcategoría', 'Presupuesto', 'Gasto Real', '% Ejec.', 'Diferencia']],
    body: tableData,
    theme: 'grid',
    headStyles: {
      fillColor: COLORS.primary,
      textColor: COLORS.white,
      fontStyle: 'bold',
      halign: 'center'
    },
    columnStyles: {
      0: { halign: 'left', fontStyle: 'bold' },
      1: { halign: 'left' },
      2: { halign: 'right' },
      3: { halign: 'right' },
      4: { halign: 'center' },
      5: { halign: 'right' }
    },
    styles: {
      fontSize: 8,
      cellPadding: 3
    },
    alternateRowStyles: {
      fillColor: COLORS.lightGray
    },
    didParseCell: (data) => {
      // Style totals row
      if (data.row.index === tableData.length - 1) {
        data.cell.styles.fillColor = COLORS.primary;
        data.cell.styles.textColor = COLORS.white;
        data.cell.styles.fontStyle = 'bold';
      }
      // Color for percentage column
      if (data.column.index === 4 && data.row.index < tableData.length - 1) {
        const pct = parseFloat(data.cell.raw);
        if (pct > 100) {
          data.cell.styles.textColor = COLORS.danger;
        } else if (pct > 80) {
          data.cell.styles.textColor = COLORS.accent;
        } else {
          data.cell.styles.textColor = COLORS.success;
        }
      }
    }
  });
  
  addFooter(doc);
  const periodSuffix = periodLabel ? periodLabel.replace(/\s+/g, '_') : year;
  doc.save(`Reporte_Presupuesto_${parkName.replace(/\s+/g, '_')}_${periodSuffix}.pdf`);
};

export const generateAccountStatementPDF = (parkName, year, accountStatement) => {
  const doc = new jsPDF('p', 'mm', 'a4');
  let yPos = addHeader(doc, parkName, `Año ${year}`, 'ESTADO DE CUENTA');
  
  // Lot info
  doc.setFillColor(...COLORS.lightGray);
  doc.rect(15, yPos, 180, 45, 'F');
  
  const lotNumber = accountStatement?.lot?.numero_lote || 'N/A';
  const propietario = accountStatement?.lot?.propietario_nombre || 'No asignado';
  const metros = accountStatement?.lot?.metros_cuadrados?.toLocaleString() || '0';
  const indiviso = accountStatement?.lot?.indiviso?.toFixed(4) || '0';
  
  doc.setFontSize(14);
  doc.setTextColor(COLORS.primary[0], COLORS.primary[1], COLORS.primary[2]);
  doc.setFont('helvetica', 'bold');
  doc.text(`Lote: ${lotNumber}`, 20, yPos + 12);
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(COLORS.gray[0], COLORS.gray[1], COLORS.gray[2]);
  doc.text(`Propietario: ${propietario}`, 20, yPos + 22);
  doc.text(`m²: ${metros} | Indiviso: ${indiviso}%`, 20, yPos + 30);
  
  // Summary on right side with IVA breakdown
  const totalCargosBase = accountStatement?.summary?.total_cargos_base || 0;
  const totalCargosIva = accountStatement?.summary?.total_cargos_iva || 0;
  const totalCargosTotal = accountStatement?.summary?.total_cargos_total || accountStatement?.summary?.total_cargos || 0;
  const totalAbonosBase = accountStatement?.summary?.total_abonos_base || 0;
  const totalAbonosIva = accountStatement?.summary?.total_abonos_iva || 0;
  const totalAbonosTotal = accountStatement?.summary?.total_abonos_total || accountStatement?.summary?.total_abonos || 0;
  const saldoActualBase = accountStatement?.summary?.saldo_actual_base || 0;
  const saldoActualIva = accountStatement?.summary?.saldo_actual_iva || 0;
  const saldoActualTotal = accountStatement?.summary?.saldo_actual_total || accountStatement?.summary?.saldo_actual || 0;
  
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(COLORS.primary[0], COLORS.primary[1], COLORS.primary[2]);
  doc.text('Total Cargos:', 110, yPos + 10);
  doc.text('Total Pagos:', 110, yPos + 22);
  doc.text('Saldo Actual:', 110, yPos + 34);
  
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(COLORS.danger[0], COLORS.danger[1], COLORS.danger[2]);
  doc.text(formatCurrency(totalCargosTotal), 180, yPos + 10, { align: 'right' });
  doc.setFontSize(7);
  doc.setTextColor(COLORS.gray[0], COLORS.gray[1], COLORS.gray[2]);
  doc.text(`Base: ${formatCurrency(totalCargosBase)} + IVA: ${formatCurrency(totalCargosIva)}`, 180, yPos + 16, { align: 'right' });
  
  doc.setFontSize(9);
  doc.setTextColor(COLORS.success[0], COLORS.success[1], COLORS.success[2]);
  doc.text(formatCurrency(totalAbonosTotal), 180, yPos + 22, { align: 'right' });
  doc.setFontSize(7);
  doc.setTextColor(COLORS.gray[0], COLORS.gray[1], COLORS.gray[2]);
  doc.text(`Base: ${formatCurrency(totalAbonosBase)} + IVA: ${formatCurrency(totalAbonosIva)}`, 180, yPos + 28, { align: 'right' });
  
  doc.setFontSize(9);
  if (saldoActualTotal > 0) {
    doc.setTextColor(COLORS.danger[0], COLORS.danger[1], COLORS.danger[2]);
  } else {
    doc.setTextColor(COLORS.success[0], COLORS.success[1], COLORS.success[2]);
  }
  doc.setFont('helvetica', 'bold');
  doc.text(formatCurrency(saldoActualTotal), 180, yPos + 34, { align: 'right' });
  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(COLORS.gray[0], COLORS.gray[1], COLORS.gray[2]);
  doc.text(`Base: ${formatCurrency(saldoActualBase)} + IVA: ${formatCurrency(saldoActualIva)}`, 180, yPos + 40, { align: 'right' });
  
  yPos += 55;
  
  // Movements table with IVA breakdown
  doc.setFontSize(12);
  doc.setTextColor(COLORS.primary[0], COLORS.primary[1], COLORS.primary[2]);
  doc.setFont('helvetica', 'bold');
  doc.text('Movimientos', 15, yPos);
  yPos += 8;
  
  const movements = accountStatement?.movements || [];
  const tableData = movements.map(mov => {
    const isCharge = (mov.cargo_total || mov.cargo || 0) > 0;
    const baseAmount = isCharge ? (mov.cargo_base || mov.cargo || 0) : (mov.abono_base || mov.abono || 0);
    const ivaAmount = isCharge ? (mov.cargo_iva || 0) : (mov.abono_iva || 0);
    const totalAmount = isCharge ? (mov.cargo_total || mov.cargo || 0) : (mov.abono_total || mov.abono || 0);
    const prefix = isCharge ? '' : '-';
    
    return [
      mov.fecha || '',
      mov.concepto || '',
      totalAmount > 0 ? `${prefix}${formatCurrency(baseAmount)}` : '-',
      totalAmount > 0 ? `${prefix}${formatCurrency(ivaAmount)}` : '-',
      totalAmount > 0 ? `${prefix}${formatCurrency(totalAmount)}` : '-',
      formatCurrency(mov.saldo_total || mov.saldo || 0)
    ];
  });
  
  if (tableData.length === 0) {
    tableData.push(['', 'Sin movimientos registrados', '', '', '', '']);
  }
  
  autoTable(doc, {
    startY: yPos,
    head: [['Fecha', 'Concepto', 'Base', 'IVA (16%)', 'Total', 'Saldo']],
    body: tableData,
    theme: 'grid',
    headStyles: {
      fillColor: COLORS.primary,
      textColor: COLORS.white,
      fontStyle: 'bold',
      halign: 'center'
    },
    columnStyles: {
      0: { halign: 'center', cellWidth: 22 },
      1: { halign: 'left', cellWidth: 55 },
      2: { halign: 'right', cellWidth: 25 },
      3: { halign: 'right', cellWidth: 25 },
      4: { halign: 'right', cellWidth: 25 },
      5: { halign: 'right', cellWidth: 25 }
    },
    styles: {
      fontSize: 8,
      cellPadding: 3
    },
    alternateRowStyles: {
      fillColor: COLORS.lightGray
    }
  });
  
  addFooter(doc);
  doc.save(`Estado_Cuenta_${accountStatement.lot.numero_lote}_${year}.pdf`);
};

// ==================== NEW REPORTS ====================

export const generateCollectionReportPDF = (parkName, year, collectionReport, periodLabel = null) => {
  const doc = new jsPDF('l', 'mm', 'a4'); // Landscape for more columns
  const displayPeriod = periodLabel || `Año ${year}`;
  let yPos = addHeader(doc, parkName, displayPeriod, 'REPORTE DE COBRANZA');
  
  // Summary cards
  doc.setFillColor(...COLORS.lightGray);
  doc.rect(15, yPos, 260, 30, 'F');
  
  const summary = collectionReport?.summary || {};
  const totals = collectionReport?.totals || {};
  
  const summaryItems = [
    { label: 'Total Lotes', value: summary.total_lotes, isNumber: true },
    { label: 'Al Corriente', value: summary.lotes_al_corriente, color: COLORS.success, isNumber: true },
    { label: 'Con Adeudo', value: summary.lotes_con_adeudo, color: COLORS.danger, isNumber: true },
    { label: '% Cobranza', value: `${summary.porcentaje_cobranza}%`, color: COLORS.primary },
    { label: 'Total Adeudo', value: formatCurrency(totals.total_saldo), color: COLORS.danger }
  ];
  
  const cardWidth = 52;
  summaryItems.forEach((item, index) => {
    const xPos = 15 + (index * cardWidth);
    doc.setFontSize(8);
    doc.setTextColor(...COLORS.gray);
    doc.text(item.label, xPos + 5, yPos + 10);
    
    doc.setFontSize(item.isNumber ? 16 : 12);
    doc.setTextColor(...(item.color || COLORS.primary));
    doc.setFont('helvetica', 'bold');
    doc.text(String(item.isNumber ? item.value : item.value), xPos + 5, yPos + 22);
    doc.setFont('helvetica', 'normal');
  });
  
  yPos += 40;
  
  // Table
  doc.setFontSize(12);
  doc.setTextColor(...COLORS.primary);
  doc.setFont('helvetica', 'bold');
  doc.text('Detalle por Lote', 15, yPos);
  yPos += 8;
  
  const tableData = collectionReport?.data?.map(row => [
    row.numero_lote,
    row.propietario || '-',
    `${row.indiviso}%`,
    formatCurrency(row.cuotas_base),
    formatCurrency(row.pagado_base),
    formatCurrency(row.saldo_base),
    formatCurrency(row.saldo_iva),
    formatCurrency(row.saldo_total),
    row.estado
  ]) || [];
  
  autoTable(doc, {
    startY: yPos,
    head: [['Lote', 'Propietario', 'Indiviso', 'Cuotas', 'Pagado', 'Saldo Base', 'Saldo IVA', 'Saldo Total', 'Estado']],
    body: tableData,
    theme: 'grid',
    headStyles: {
      fillColor: COLORS.primary,
      textColor: COLORS.white,
      fontStyle: 'bold',
      halign: 'center',
      fontSize: 8
    },
    columnStyles: {
      0: { halign: 'left', fontStyle: 'bold', cellWidth: 20 },
      1: { halign: 'left', cellWidth: 45 },
      2: { halign: 'center', cellWidth: 18 },
      3: { halign: 'right', cellWidth: 25 },
      4: { halign: 'right', cellWidth: 25 },
      5: { halign: 'right', cellWidth: 25 },
      6: { halign: 'right', cellWidth: 22 },
      7: { halign: 'right', cellWidth: 25 },
      8: { halign: 'center', cellWidth: 35 }
    },
    styles: {
      fontSize: 7,
      cellPadding: 2
    },
    alternateRowStyles: {
      fillColor: COLORS.lightGray
    },
    didParseCell: (data) => {
      // Highlight rows with debt
      if (data.row.index < tableData.length) {
        const rowData = collectionReport?.data?.[data.row.index];
        if (rowData && rowData.saldo_base > 0 && data.column.index === 8) {
          data.cell.styles.textColor = COLORS.danger;
          data.cell.styles.fontStyle = 'bold';
        }
      }
    }
  });
  
  addFooter(doc);
  const periodSuffix = periodLabel ? periodLabel.replace(/\s+/g, '_') : year;
  doc.save(`Reporte_Cobranza_${parkName.replace(/\s+/g, '_')}_${periodSuffix}.pdf`);
};

export const generateBudgetDetailPDF = (parkName, year, budgetDetail) => {
  const doc = new jsPDF('p', 'mm', 'a4');
  let yPos = addHeader(doc, parkName, `Año ${year}`, 'PRESUPUESTO ANUAL');
  
  const totals = budgetDetail?.totals || {};
  const summary = budgetDetail?.summary || {};
  
  // Summary cards
  doc.setFillColor(...COLORS.lightGray);
  doc.rect(15, yPos, 180, 35, 'F');
  
  const summaryItems = [
    { label: 'Total Anual', value: formatCurrency(totals.total_anual) },
    { label: 'Total Mensual', value: formatCurrency(totals.total_mensual) },
    { label: 'Mensual + IVA', value: formatCurrency(totals.total_mensual_con_iva), color: COLORS.accent }
  ];
  
  const cardWidth = 60;
  summaryItems.forEach((item, index) => {
    const xPos = 15 + (index * cardWidth);
    doc.setFontSize(8);
    doc.setTextColor(...COLORS.gray);
    doc.text(item.label, xPos + 5, yPos + 12);
    
    doc.setFontSize(14);
    doc.setTextColor(...(item.color || COLORS.primary));
    doc.setFont('helvetica', 'bold');
    doc.text(item.value, xPos + 5, yPos + 26);
    doc.setFont('helvetica', 'normal');
  });
  
  yPos += 45;
  
  // Build table with categories and subcategories
  doc.setFontSize(12);
  doc.setTextColor(...COLORS.primary);
  doc.setFont('helvetica', 'bold');
  doc.text('Detalle del Presupuesto', 15, yPos);
  yPos += 8;
  
  const tableData = [];
  let grandTotalAnual = 0;
  
  budgetDetail?.data?.forEach(cat => {
    // Category header row
    tableData.push([
      { content: cat.categoria, styles: { fontStyle: 'bold', fillColor: [226, 232, 240] } },
      { content: '', styles: { fillColor: [226, 232, 240] } },
      { content: formatCurrency(cat.total_anual), styles: { fontStyle: 'bold', fillColor: [226, 232, 240] } },
      { content: formatCurrency(cat.total_mensual), styles: { fontStyle: 'bold', fillColor: [226, 232, 240] } }
    ]);
    
    // Subcategory rows
    cat.subcategorias?.forEach(sub => {
      tableData.push([
        '',
        sub.subcategoria,
        formatCurrency(sub.importe_anual),
        formatCurrency(sub.importe_mensual)
      ]);
    });
    
    grandTotalAnual += cat.total_anual;
  });
  
  // Grand total row
  tableData.push([
    { content: 'TOTAL PRESUPUESTO', colSpan: 2, styles: { fontStyle: 'bold' } },
    { content: formatCurrency(grandTotalAnual), styles: { fontStyle: 'bold' } },
    { content: formatCurrency(grandTotalAnual / 12), styles: { fontStyle: 'bold' } }
  ]);
  
  autoTable(doc, {
    startY: yPos,
    head: [['Categoría', 'Subcategoría', 'Importe Anual', 'Importe Mensual']],
    body: tableData,
    theme: 'grid',
    headStyles: {
      fillColor: COLORS.primary,
      textColor: COLORS.white,
      fontStyle: 'bold',
      halign: 'center'
    },
    columnStyles: {
      0: { halign: 'left', cellWidth: 50 },
      1: { halign: 'left', cellWidth: 60 },
      2: { halign: 'right', cellWidth: 35 },
      3: { halign: 'right', cellWidth: 35 }
    },
    styles: {
      fontSize: 9,
      cellPadding: 3
    },
    didParseCell: (data) => {
      // Style totals row
      if (data.row.index === tableData.length - 1) {
        data.cell.styles.fillColor = COLORS.primary;
        data.cell.styles.textColor = COLORS.white;
      }
    }
  });
  
  addFooter(doc);
  doc.save(`Presupuesto_${parkName.replace(/\s+/g, '_')}_${year}.pdf`);
};

export const generateFeesByLotPDF = (parkName, year, feesReport, periodLabel = null) => {
  const doc = new jsPDF('l', 'mm', 'a4'); // Landscape
  const displayPeriod = periodLabel || `Año ${year}`;
  let yPos = addHeader(doc, parkName, displayPeriod, 'REPORTE DE CUOTAS POR LOTE');
  
  const totals = feesReport?.totals || {};
  const summary = feesReport?.summary || {};
  
  // Summary
  doc.setFillColor(...COLORS.lightGray);
  doc.rect(15, yPos, 260, 30, 'F');
  
  const summaryItems = [
    { label: 'Total Lotes', value: String(summary.total_lotes), isNumber: true },
    { label: 'Meses en Período', value: String(summary.meses_en_periodo), isNumber: true },
    { label: 'Total Mensual (Base)', value: formatCurrency(totals.total_base_mensual) },
    { label: 'Total Mensual (IVA)', value: formatCurrency(totals.total_iva_mensual) },
    { label: 'Total Mensual', value: formatCurrency(totals.total_mensual), color: COLORS.accent }
  ];
  
  const cardWidth = 52;
  summaryItems.forEach((item, index) => {
    const xPos = 15 + (index * cardWidth);
    doc.setFontSize(8);
    doc.setTextColor(...COLORS.gray);
    doc.text(item.label, xPos + 5, yPos + 10);
    
    doc.setFontSize(item.isNumber ? 16 : 11);
    doc.setTextColor(...(item.color || COLORS.primary));
    doc.setFont('helvetica', 'bold');
    doc.text(item.value, xPos + 5, yPos + 22);
    doc.setFont('helvetica', 'normal');
  });
  
  yPos += 40;
  
  // Table
  doc.setFontSize(12);
  doc.setTextColor(...COLORS.primary);
  doc.setFont('helvetica', 'bold');
  doc.text('Cuotas por Lote', 15, yPos);
  yPos += 8;
  
  const tableData = feesReport?.data?.map(row => [
    row.numero_lote,
    row.propietario || '-',
    row.metros_cuadrados ? `${row.metros_cuadrados} m²` : '-',
    `${row.indiviso}%`,
    formatCurrency(row.cuota_base_mensual),
    formatCurrency(row.cuota_iva_mensual),
    formatCurrency(row.cuota_total_mensual),
    formatCurrency(row.cuota_total_periodo)
  ]) || [];
  
  // Totals row
  tableData.push([
    'TOTAL',
    '',
    '',
    '',
    formatCurrency(totals.total_base_mensual),
    formatCurrency(totals.total_iva_mensual),
    formatCurrency(totals.total_mensual),
    formatCurrency(totals.total_periodo)
  ]);
  
  autoTable(doc, {
    startY: yPos,
    head: [['Lote', 'Propietario', 'Superficie', 'Indiviso', 'Base/Mes', 'IVA/Mes', 'Total/Mes', 'Total Período']],
    body: tableData,
    theme: 'grid',
    headStyles: {
      fillColor: COLORS.primary,
      textColor: COLORS.white,
      fontStyle: 'bold',
      halign: 'center',
      fontSize: 9
    },
    columnStyles: {
      0: { halign: 'left', fontStyle: 'bold', cellWidth: 22 },
      1: { halign: 'left', cellWidth: 50 },
      2: { halign: 'center', cellWidth: 25 },
      3: { halign: 'center', cellWidth: 20 },
      4: { halign: 'right', cellWidth: 28 },
      5: { halign: 'right', cellWidth: 25 },
      6: { halign: 'right', cellWidth: 28 },
      7: { halign: 'right', cellWidth: 32 }
    },
    styles: {
      fontSize: 8,
      cellPadding: 2
    },
    alternateRowStyles: {
      fillColor: COLORS.lightGray
    },
    didParseCell: (data) => {
      if (data.row.index === tableData.length - 1) {
        data.cell.styles.fillColor = COLORS.primary;
        data.cell.styles.textColor = COLORS.white;
        data.cell.styles.fontStyle = 'bold';
      }
    }
  });
  
  addFooter(doc);
  const periodSuffix = periodLabel ? periodLabel.replace(/\s+/g, '_') : year;
  doc.save(`Reporte_Cuotas_${parkName.replace(/\s+/g, '_')}_${periodSuffix}.pdf`);
};

export const generateExpensesDetailPDF = (parkName, year, expensesReport, periodLabel = null, activeFilters = '') => {
  const doc = new jsPDF('landscape');
  const displayPeriod = periodLabel || `Año ${year}`;
  const yPos = addHeader(doc, parkName, displayPeriod, 'Reporte Detallado de Gastos');
  
  const pageWidth = doc.internal.pageSize.getWidth();
  
  // Filters info
  if (activeFilters) {
    doc.setFontSize(9);
    doc.setTextColor(...COLORS.gray);
    doc.text(`Filtros: ${activeFilters}`, 20, yPos);
  }
  
  const tableStartY = activeFilters ? yPos + 8 : yPos;
  
  // Summary cards
  doc.setFillColor(...COLORS.lightGray);
  doc.roundedRect(20, tableStartY, 80, 20, 2, 2, 'F');
  doc.roundedRect(110, tableStartY, 80, 20, 2, 2, 'F');
  
  doc.setFontSize(8);
  doc.setTextColor(...COLORS.gray);
  doc.text('TOTAL GASTOS', 25, tableStartY + 8);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.danger);
  doc.text(formatCurrency(expensesReport?.totals?.total_gastos || 0), 25, tableStartY + 17);
  
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...COLORS.gray);
  doc.text('REGISTROS', 115, tableStartY + 8);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.primary);
  doc.text(String(expensesReport?.totals?.total_registros || 0), 115, tableStartY + 17);
  
  // Table
  const tableData = (expensesReport?.data || []).map(row => [
    row.fecha || '',
    row.categoria || '',
    row.subcategoria || '',
    row.tipo_cuota === 'extraordinario' ? 'Extra' : 'Ordinario',
    row.proveedor || '',
    row.descripcion || '',
    formatCurrency(row.monto)
  ]);
  
  // Total row
  tableData.push([
    'TOTAL', '', '', '', '', '',
    formatCurrency(expensesReport?.totals?.total_gastos || 0)
  ]);
  
  autoTable(doc, {
    startY: tableStartY + 28,
    head: [['Fecha', 'Categoría', 'Subcategoría', 'Tipo', 'Proveedor', 'Descripción', 'Monto']],
    body: tableData,
    theme: 'grid',
    headStyles: {
      fillColor: COLORS.primary,
      textColor: COLORS.white,
      fontStyle: 'bold',
      halign: 'center',
      fontSize: 9
    },
    columnStyles: {
      0: { halign: 'left', cellWidth: 22 },
      1: { halign: 'left', cellWidth: 35 },
      2: { halign: 'left', cellWidth: 35 },
      3: { halign: 'center', cellWidth: 22 },
      4: { halign: 'left', cellWidth: 40 },
      5: { halign: 'left', cellWidth: 'auto' },
      6: { halign: 'right', cellWidth: 28 }
    },
    styles: {
      fontSize: 8,
      cellPadding: 3
    },
    alternateRowStyles: {
      fillColor: COLORS.lightGray
    },
    didParseCell: (data) => {
      if (data.row.index === tableData.length - 1) {
        data.cell.styles.fillColor = COLORS.primary;
        data.cell.styles.textColor = COLORS.white;
        data.cell.styles.fontStyle = 'bold';
      }
    }
  });
  
  addFooter(doc);
  const periodSuffix = periodLabel ? periodLabel.replace(/\s+/g, '_') : year;
  doc.save(`Reporte_Gastos_${parkName.replace(/\s+/g, '_')}_${periodSuffix}.pdf`);
};
